import { minus_circle_disabled, minus_circle_enabled, plus_circle_disabled, plus_circle_enabled } from '@assets/icons';
import { ChipXButton, Text } from '@components';
import { styleConstant } from '@constant';
import { Button } from '@mui/material';
import { colorSet } from '@theme';
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

interface ISimpleCharacterNumberInputProps {
  characterNumber: {
    maleNumber: number;
    femaleNumber: number;
  } | null;
  disabled?: boolean;
  onApplyClick: (values: { maleNumber: number; femaleNumber: number }) => void;
  onChipXButtonClick?: () => void;
  isError?: boolean;
}

// CharacterNumberInput: 성별무관 선택 가능 & 남녀 합 3 초과 불가 & 4 이상은 4+로 표기
// SimpleCharacterNumberInput: 성별무관 선택 불가 & 남녀 각각 0~3명 설정 가능 & 남녀 합 1 이상일 시 적용 버튼 활성화
export const SimpleCharacterNumberInput = (props: ISimpleCharacterNumberInputProps) => {
  const { characterNumber, disabled = false, onApplyClick, onChipXButtonClick, isError = false } = props;

  const [isSelectPaperOpen, setIsSelectPaperOpen] = useState<boolean>(false);
  const [displayText, setDisplayText] = useState<string | null>(
    !characterNumber ? null : `남자 ${characterNumber.maleNumber}명 ∙ 여자 ${characterNumber.femaleNumber}명`
  );
  const [initialCharacterNumber, setInitialCharacterNumber] = useState<{
    maleNumber: number;
    femaleNumber: number;
  }>({
    maleNumber: characterNumber?.maleNumber ?? 0,
    femaleNumber: characterNumber?.femaleNumber ?? 0
  });
  const [tempCharacterNumber, setTempCharacterNumber] = useState<{
    maleNumber: number;
    femaleNumber: number;
  }>({
    maleNumber: characterNumber?.maleNumber ?? 0,
    femaleNumber: characterNumber?.femaleNumber ?? 0
  });

  const selectRef = useRef<HTMLDivElement>(null);

  const handleNumberChange = (type: 'male' | 'female', action: 'increase' | 'decrease') => {
    const currentValue =
      type === 'male' ? (tempCharacterNumber?.maleNumber ?? 0) : (tempCharacterNumber?.femaleNumber ?? 0);

    if (action === 'increase') {
      if (type === 'male') {
        setTempCharacterNumber({ ...tempCharacterNumber, maleNumber: currentValue + 1 });
      } else {
        setTempCharacterNumber({ ...tempCharacterNumber, femaleNumber: currentValue + 1 });
      }
    } else if (action === 'decrease') {
      if (type === 'male') {
        setTempCharacterNumber({ ...tempCharacterNumber, maleNumber: currentValue - 1 });
      } else {
        setTempCharacterNumber({ ...tempCharacterNumber, femaleNumber: currentValue - 1 });
      }
    }
  };

  const isApplyEnabled = () => {
    const hasChanged =
      tempCharacterNumber.maleNumber !== initialCharacterNumber.maleNumber ||
      tempCharacterNumber.femaleNumber !== initialCharacterNumber.femaleNumber;

    // 남녀 둘의 합이 1 이상일 시 true
    const hasValidSum = tempCharacterNumber.maleNumber + tempCharacterNumber.femaleNumber > 0;

    return hasChanged && hasValidSum;
  };

  const handleApplyClick = () => {
    const newText = `남자 ${tempCharacterNumber?.maleNumber}명 ∙ 여자 ${tempCharacterNumber?.femaleNumber}명`;
    setDisplayText(newText);
    setIsSelectPaperOpen(false);

    setInitialCharacterNumber({
      maleNumber: tempCharacterNumber?.maleNumber ?? 0,
      femaleNumber: tempCharacterNumber?.femaleNumber ?? 0
    });

    // 적용 버튼 클릭 시 현재 값들을 상위 컴포넌트로 전달
    onApplyClick({
      maleNumber: tempCharacterNumber?.maleNumber ?? 0,
      femaleNumber: tempCharacterNumber?.femaleNumber ?? 0
    });
  };

  const handleChipXButtonClick = () => {
    setDisplayText(null);
    setIsSelectPaperOpen(false);

    setInitialCharacterNumber({
      maleNumber: characterNumber?.maleNumber ?? 0,
      femaleNumber: characterNumber?.femaleNumber ?? 0
    });

    setTempCharacterNumber({
      maleNumber: characterNumber?.maleNumber ?? 0,
      femaleNumber: characterNumber?.femaleNumber ?? 0
    });

    if (onChipXButtonClick) {
      onChipXButtonClick();
    }
  };

  const NumberControl = ({
    value,
    onIncrease,
    onDecrease
  }: {
    value: number;
    onIncrease: () => void;
    onDecrease: () => void;
  }) => (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        backgroundColor: colorSet.gray100,
        borderRadius: 20,
        padding: '4px'
      }}
    >
      <img
        onClick={() => {
          if (value === 0) return;
          onDecrease();
        }}
        src={value === 0 ? minus_circle_disabled : minus_circle_enabled}
        alt=""
        style={{ width: 32, height: 32, cursor: value === 0 ? 'default' : 'pointer' }}
      />

      <span style={{ width: '40px', textAlign: 'center' }}>
        <Text variant="subtitle" fontWeight="bold" color={colorSet.gray600}>
          {value}
        </Text>
      </span>

      <img
        onClick={() => {
          if (value === 3) return;
          onIncrease();
        }}
        src={value === 3 ? plus_circle_disabled : plus_circle_enabled}
        alt=""
        style={{
          width: 32,
          height: 32,
          cursor: value === 3 ? 'default' : 'pointer'
        }}
      />
    </div>
  );

  // Select 바깥 영역 클릭시 Paper 닫고 기존 값으로 돌아가기
  const resetToLastSavedValues = () => {
    setTempCharacterNumber({
      maleNumber: initialCharacterNumber.maleNumber,
      femaleNumber: initialCharacterNumber.femaleNumber
    });
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const selectPaper = document.querySelector('.selectPaper');
      if (
        selectRef.current &&
        !selectRef.current.contains(event.target as Node) &&
        selectPaper &&
        !selectPaper.contains(event.target as Node)
      ) {
        setIsSelectPaperOpen(false);
        resetToLastSavedValues();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [initialCharacterNumber]);

  // characterNumber prop value가 업데이트될 시 displayText, initialCharacterNumber, tempCharacterNumber setState
  useEffect(() => {
    if (!characterNumber) {
      setDisplayText(null);
      setInitialCharacterNumber({
        maleNumber: 0,
        femaleNumber: 0
      });
      setTempCharacterNumber({
        maleNumber: 0,
        femaleNumber: 0
      });
    } else {
      setDisplayText(`남자 ${characterNumber.maleNumber}명 ∙ 여자 ${characterNumber.femaleNumber}명`);
      setInitialCharacterNumber({
        maleNumber: characterNumber.maleNumber ?? 0,
        femaleNumber: characterNumber.femaleNumber ?? 0
      });
      setTempCharacterNumber({
        maleNumber: characterNumber.maleNumber ?? 0,
        femaleNumber: characterNumber.femaleNumber ?? 0
      });
    }
  }, [characterNumber]);

  return (
    <>
      <div className="characterNumberSelect" style={{ display: 'inline-flex', position: 'relative' }} ref={selectRef}>
        <div style={{ position: 'relative', display: 'inline-flex', whiteSpace: 'nowrap' }}>
          {!displayText ? (
            <div
              style={{
                height: 38,
                padding: '9px 16px',
                display: 'inline-flex',
                justifyContent: 'center',
                alignItems: 'center',
                borderRadius: '12px',
                border: `1px solid ${isSelectPaperOpen ? colorSet.primaryMain : isError ? colorSet.errorMain : colorSet.gray300}`,
                backgroundColor: isSelectPaperOpen ? 'rgba(119, 78, 255, 0.15)' : 'transparent',
                cursor: 'pointer'
              }}
              onClick={() => !disabled && setIsSelectPaperOpen(!isSelectPaperOpen)}
            >
              <Text variant="body1">인원수</Text>
            </div>
          ) : (
            <div
              style={{
                height: 38,
                padding: '9px 16px',
                display: 'inline-flex',
                justifyContent: 'center',
                alignItems: 'center',
                borderRadius: '12px',
                border: `1px solid ${isSelectPaperOpen ? colorSet.primaryMain : colorSet.gray600}`,
                backgroundColor: isSelectPaperOpen ? 'rgba(119, 78, 255, 0.15)' : colorSet.gray200,
                cursor: 'pointer'
              }}
              onClick={() => !disabled && setIsSelectPaperOpen(!isSelectPaperOpen)}
            >
              <Text variant="body1" fontWeight="bold" style={{ marginRight: 8 }}>
                인원수
              </Text>
              {displayText ? <Text variant="body1">{displayText}</Text> : null}
              <ChipXButton onClick={handleChipXButtonClick} />
            </div>
          )}
        </div>

        {isSelectPaperOpen &&
          !disabled &&
          createPortal(
            <div
              className="selectPaper"
              onClick={(e) => e.stopPropagation()} // 이벤트 버블링 방지
              style={{
                position: 'fixed', // absolute 대신 fixed 사용
                top: selectRef.current?.getBoundingClientRect().bottom
                  ? selectRef.current?.getBoundingClientRect().bottom + 10
                  : 0,
                left: selectRef.current?.getBoundingClientRect().left ?? 0,
                zIndex: styleConstant.zIndexChipPaper,
                width: '274px',
                padding: '12px 16px',
                backgroundColor: colorSet.white,
                boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.1)'
              }}
            >
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '10px',
                  marginBottom: '12px'
                }}
              >
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div style={{ width: 100, display: 'flex', justifyContent: 'center' }}>
                      <Text variant="body1">남자</Text>
                    </div>
                    <NumberControl
                      value={tempCharacterNumber?.maleNumber ?? 0}
                      onIncrease={() => handleNumberChange('male', 'increase')}
                      onDecrease={() => handleNumberChange('male', 'decrease')}
                    />
                  </div>

                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div style={{ width: 100, display: 'flex', justifyContent: 'center' }}>
                      <Text variant="body1">여자</Text>
                    </div>
                    <NumberControl
                      value={tempCharacterNumber?.femaleNumber ?? 0}
                      onIncrease={() => handleNumberChange('female', 'increase')}
                      onDecrease={() => handleNumberChange('female', 'decrease')}
                    />
                  </div>
                </div>
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  disabled={!isApplyEnabled()}
                  variant="contained"
                  onClick={() => {
                    setIsSelectPaperOpen(false);
                    handleApplyClick();
                  }}
                  sx={{ width: 90, height: 44, borderRadius: 22 }}
                >
                  적용
                </Button>
              </div>
            </div>,
            document.body
          )}
      </div>
    </>
  );
};
